import { MedplumClient } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { fetchCptCodeQuestionnaire } from "@/encounters/services/questionnaire";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

import { getEncounterCPTCodeQuestionnaireResponse } from "../utils/questionnaire-response";

type HandleQuestionnaireResponseCreationParams = {
  medplum: MedplumClient;
  cptCodeData: ProceduresFormTypeWithId[];
  status: QuestionnaireResponse["status"];
  patientId?: string;
  encounterId?: string;
  previousQuestionnaireResponse?: QuestionnaireResponse;
  mainQuestionnaireUrl: string;
};

type ProceduresSaveResult = {
  savedProcedures: { procedureId: string; questionnaireResponse: QuestionnaireResponse }[];
  failures: unknown[];
};

/**
 * Saves one CPT-code QuestionnaireResponse per procedure. Each write settles on its own, so a
 * failure does not hide the procedures that did save.
 *
 * @returns The procedures that saved, with their responses, and the errors of those that did not.
 */
export const handleQuestionnaireResponseCreation = async ({
  medplum,
  cptCodeData,
  status,
  patientId,
  encounterId,
  mainQuestionnaireUrl,
}: HandleQuestionnaireResponseCreationParams): Promise<ProceduresSaveResult> => {
  const questionnaire = await fetchCptCodeQuestionnaire(medplum);

  if (!questionnaire || !patientId || !encounterId) {
    // Staged procedures that cannot be written are a failed save, not an empty one.
    const failures = cptCodeData.length
      ? [new Error("The procedures could not be saved: a save prerequisite is missing")]
      : [];
    return { savedProcedures: [], failures };
  }

  const [mainQuestionnaireResponse] = await fetchAmendmentHistoryByQuestionnaireUrl(
    medplum,
    mainQuestionnaireUrl,
    encounterId,
  );

  const lastParentQuestionnaireResponseVersionId = mainQuestionnaireResponse?.[0]?.meta?.versionId;

  const cptQuestionnaireResponsePromises = cptCodeData.map(async (cptCode) => {
    const hasQuestionnaireResponse = cptCode.exists;

    const questionnaireResponse = getEncounterCPTCodeQuestionnaireResponse({
      cptCode,
      status,
      patientId,
      encounterId,
      questionnaireId: questionnaire.id!,
      questionnaireResponseId: hasQuestionnaireResponse ? cptCode.id : null,
      versionId: lastParentQuestionnaireResponseVersionId,
    });

    if (hasQuestionnaireResponse) return medplum.updateResource(questionnaireResponse);

    return medplum.createResource(questionnaireResponse);
  });

  const saveResults = await Promise.allSettled(cptQuestionnaireResponsePromises);

  return saveResults.reduce<ProceduresSaveResult>(
    (result, saveResult, index) => {
      if (saveResult.status === "fulfilled") {
        result.savedProcedures.push({
          procedureId: cptCodeData[index].id,
          questionnaireResponse: saveResult.value,
        });
      } else {
        result.failures.push(saveResult.reason);
      }
      return result;
    },
    { savedProcedures: [], failures: [] },
  );
};

export const fetchProceduresQuestionnaireResponse = async (
  medplum: MedplumClient,
  patientId?: string,
  encounterId?: string,
) => {
  if (!encounterId || !patientId) return [];

  const procedureQuestionnaire = await fetchCptCodeQuestionnaire(medplum);

  if (!procedureQuestionnaire) return [];

  const questionnaireResponseResults = await medplum.searchResources(
    "QuestionnaireResponse",
    {
      encounter: joinReferenceString("Encounter", encounterId),
      questionnaire: joinReferenceString("Questionnaire", procedureQuestionnaire.id!),
      subject: joinReferenceString("Patient", patientId),
      _sort: "-_lastUpdated",
    },
    {
      cache: "no-cache",
    },
  );

  const parsedQuestionnaireResponses = getResourcesFromBundle(
    "QuestionnaireResponse",
    questionnaireResponseResults.bundle,
  );

  return parsedQuestionnaireResponses;
};

export const softDeleteProcedureQuestionnaireResponse = async (
  medplum: MedplumClient,
  questionnaireResponseId: string,
) => {
  medplum.invalidateSearches("QuestionnaireResponse");
  const questionnaireResponse = await medplum.searchOne("QuestionnaireResponse", {
    _id: questionnaireResponseId,
  });

  if (!questionnaireResponse) return;

  return await medplum.updateResource({ ...questionnaireResponse, status: "stopped" });
};
